<script setup>
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import { VDataTableServer } from 'vuetify/labs/VDataTable'
import FishboneCategoryDrawer from '@/views/quality/master/FishboneCategoryDrawer.vue'
import FishboneCategoryService from '@/services/quality/fishboneCategory.service'

const searchQuery = ref('')
const debouncedSearch = ref('')
const isConfirmDialogVisible = ref(false)

// Debounce search 500ms so typing doesn't fire a request per keystroke
let searchTimeout = null

watch(searchQuery, val => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    debouncedSearch.value = val
    page.value = 1
  }, 500)
})

definePage({
  meta: {
    name: 'incident-master-fishbone-category',
    subject: 'quality.menu',
    action: 'master_fishbone_category',
  },  
})

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

const updateOptions = options => {
  page.value = options.page
  orderBy.value = options.sortBy[0]?.key
  sortBy.value = options.sortBy[0]?.order
}

// Headers
const headers = [
  {
    title: 'Name',
    key: 'name',
  },
  {
    // Keyed to the actual column — 'required' was not one, so sorting sent
    // an order_by the API can't resolve.
    title: 'Required',
    key: 'is_required',
  },
  {
    title: 'Actions',
    key: 'actions',
    sortable: false,
  },
]

const {
  data: fishboneCategoryListing,
  execute: fetchFishboneCategoryListing,
} =  await useApi(createUrl('/masters/fishbone-categories', {
  query: {
    paged: 1,
    q: debouncedSearch,
    itemsPerPage,
    page,
    sort: sortBy,
    order_by: orderBy,
  },
}))

const fishboneCategories = computed(() => fishboneCategoryListing.value?.data)
const totalInvestigationGroups = computed(() => fishboneCategoryListing.value?.meta?.total)

const isDrawerVisible = ref(false)
const isViewOnly = ref(false)
const fishboneCategoryData = ref()

const showInvestigationGroup = (data = null, viewOnly = false) => {
  fishboneCategoryData.value = data
  isDrawerVisible.value = true
  isViewOnly.value = viewOnly
}

watch(isDrawerVisible, value => {
  if (value === false) {
    fishboneCategoryData.value = null
    isViewOnly.value = false
  }
})

const refConfirmDialog = ref()

const showConfirmDialog = data => {
  fishboneCategoryData.value = data
  isConfirmDialogVisible.value = true
}

const deleteInvestigationGroup = async (confirm, data) => {
  if(data?.id && confirm){
    FishboneCategoryService.destroy(
      data.id, 
      () => {
        refConfirmDialog.value.onSuccess()
        isConfirmDialogVisible.value = false
        fetchFishboneCategoryListing()
      },
      () => {
        refConfirmDialog.value.onError()
      },
    )
  }
}

watch(isConfirmDialogVisible, value => {
  if (value === false) fishboneCategoryData.value = null
})
</script>

<template>
  <section>
    <VCard
      variant="flat"
      class="border"
    >
      <VCardText class="d-flex flex-wrap align-center py-4 gap-4">
        <!-- 👉 Search -->
        <div class="fishbone-search-field">
          <AppTextField
            v-model="searchQuery"
            placeholder="Search"
            density="compact"
          />
        </div>

        <VSpacer />

        <VBtn
          prepend-icon="tabler-plus"
          @click="showInvestigationGroup(null)"
        >
          Add Fishbone Category
        </VBtn>
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items="fishboneCategories"
        :items-length="totalInvestigationGroups"
        :headers="headers"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <template #item.name="{ item }">
          <button
            type="button"
            class="fishbone-name text-body-1 font-weight-medium text-start"
            @click="showInvestigationGroup(item, true)"
          >
            {{ item.name }}
          </button>
        </template>

        <!-- Mandatory vs Optional is a distinction, not a good/bad state — no success/error colouring. -->
        <template #item.is_required="{ item }">
          <VChip
            :color="item.is_required ? 'primary' : 'secondary'"
            size="small"
          >
            {{ item.is_required ? 'Mandatory' : 'Optional' }}
          </VChip>
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <IconBtn @click="showConfirmDialog(item)">
            <VIcon icon="tabler-trash" />
          </IconBtn>

          <IconBtn @click="showInvestigationGroup(item)">
            <VIcon icon="tabler-edit" />
          </IconBtn>
        </template>
      </VDataTableServer>
    </VCard>
    <FishboneCategoryDrawer
      v-model:isDrawerOpen="isDrawerVisible"
      :form-data="fishboneCategoryData"
      :view-only="isViewOnly"
      @refetch-listing="fetchFishboneCategoryListing"
    />
    <ConfirmDialog
      ref="refConfirmDialog"
      v-model:isDialogVisible="isConfirmDialogVisible"
      confirmation-question="Are you sure?"
      confirm-msg="Fishbone Category Deleted."
      confirm-title="Deleted!"
      cancel-title="Error!"
      cancel-msg="Something Went Wrong."
      :is-confirm-visible="false"
      :is-error-visible="false"
      :data="fishboneCategoryData"
      @confirm="deleteInvestigationGroup"
    />
  </section>
</template>

<style>
.v-data-table-footer {
  border-block-start: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  margin-block-start: 0 !important;
  padding-block: 0.5rem;
}
</style>

<style lang="scss" scoped>
.fishbone-search-field {
  inline-size: 100%;

  @media (min-width: 600px) {
    inline-size: 20rem;
  }
}

// Rendered as a real button so the row opens from the keyboard too,
// but styled as plain text to sit naturally in the cell.
.fishbone-name {
  padding: 0;
  border: 0;
  background: none;
  color: rgb(var(--v-theme-on-surface));
  cursor: pointer;
  font: inherit;
  transition: color 0.15s ease;

  &:hover {
    color: rgb(var(--v-theme-primary));
  }
}
</style>
